/**
 * Las rutas de las tres superficies.
 *
 * `RequireRole` decide **qué se muestra**; el Worker decide **qué se puede
 * hacer**. Si alguien escribe `/admin` en la barra sin ser administrador, acá lo
 * mandamos a su portal, y aunque burlara esto, cada endpoint lo rechazaría
 * igual: son dos capas y la de verdad es la de atrás.
 */

import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import type { AppRole } from '@cleanblue/shared';
import { homeFor, useSession } from '@/lib/session';
import { escucharAlServiceWorker, reafirmarPush } from '@/lib/push';
import { InvitarAvisos } from '@/components/common/InvitarAvisos';
import { Loading } from '@/components/ui';
import { Shell } from '@/components/Shell';

import { LoginPage } from '@/features/auth/LoginPage';
import { PublicLanding } from '@/features/public/Landing';
import { PrivacyPage, TermsPage } from '@/features/public/LegalPage';
import { AcceptInvitePage, ResetPasswordPage } from '@/features/auth/TokenPages';
import { JoinUnitPage } from '@/features/auth/JoinUnitPage';

import { AdminDashboard } from '@/features/admin/Dashboard';
import { ClientsPage } from '@/features/admin/Clients';
import { UnitDetailPage } from '@/features/admin/Units';
import { DoctorProfilePage } from '@/features/admin/DoctorProfile';
import { ContractsPage, ContractDetailPage } from '@/features/admin/Contracts';
import { GeneratePage } from '@/features/admin/Generate';
import { ChargesPage, ChargeDetailPage } from '@/features/admin/Charges';
import { CobrosLayout } from '@/features/admin/Cobros';
import { ActionsPage } from '@/features/admin/Actions';
import { MatrixPage } from '@/features/admin/Matrix';
import { PaymentsPage } from '@/features/admin/Payments';
import { InvoicingPage } from '@/features/admin/Invoicing';
import { ReconciliationPage } from '@/features/admin/Reconciliation';
import { FinancePage } from '@/features/admin/Finance';
import { InventoryPage } from '@/features/admin/Inventory';
import { ObligationsPage } from '@/features/admin/Obligations';
import { StaffPage } from '@/features/admin/Staff';
import { QualityPage } from '@/features/admin/Quality';
import { IncidentsPage } from '@/features/admin/Incidents';
import { RequestsPage } from '@/features/admin/Requests';
import { CalendarPage } from '@/features/admin/Calendar';
import { ReportsPage } from '@/features/admin/Reports';
import { CatalogPage } from '@/features/admin/Catalog';
import { SettingsPage } from '@/features/admin/Settings';
import { MessagesPage } from '@/features/admin/Messages';
import { SuppliersPage } from '@/features/admin/Suppliers';

/* «Mi perfil» es una sola pantalla para los tres shells (CB-21): el perfil de
   acceso es el mismo tenga uno el rol que tenga. Cada superficie la monta con
   su propio armazón; la pantalla no sabe desde cuál la abrieron. */
import { MyProfileMobilePage, MyProfilePage } from '@/features/profile/MyProfile';

import { PortalLayout } from '@/features/portal/PortalLayout';
import { PortalHome } from '@/features/portal/Home';
import { PortalCharges, PortalChargesIndex, PortalChargeDetail } from '@/features/portal/Charges';
import { PortalServices } from '@/features/portal/Services';
import { PortalImprove } from '@/features/portal/Improve';
import { PortalMore } from '@/features/portal/More';

import { EmployeeLayout } from '@/features/employee/EmployeeLayout';
import { EmployeeHome } from '@/features/employee/Home';
import { EmployeeInsumos } from '@/features/employee/Insumos';
import { EmployeeOvertime } from '@/features/employee/Overtime';
import { EmployeeVacations } from '@/features/employee/Vacations';
import { EmployeeVisit } from '@/features/employee/Visit';

function RequireRole({ roles, children }: { roles: AppRole[]; children: ReactNode }) {
  const { user, loading } = useSession();
  const location = useLocation();

  if (loading) return <Loading label="Verificando su sesión…" />;
  if (!user) return <Navigate to="/ingresar" state={{ from: location.pathname }} replace />;
  if (!roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;

  return <>{children}</>;
}

/** Una pantalla de administración con su armazón y sus migas. */
function AdminPage({ crumbs, children }: { crumbs: string[]; children: ReactNode }) {
  return (
    <RequireRole roles={['ADMIN']}>
      <Shell crumbs={crumbs}>{children}</Shell>
    </RequireRole>
  );
}

/** ¿Se abrió desde el ícono agregado a la pantalla de inicio, y no como una
 *  pestaña más del navegador? `display-mode: standalone` es el estándar;
 *  `navigator.standalone` es el mismo dato en Safari/iOS, que no lo expone
 *  por media query en versiones viejas. */
function esStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return window.matchMedia?.('(display-mode: standalone)').matches === true || nav.standalone === true;
}

/**
 * `cleanblue.cc` es la landing pública, **siempre** — con sesión o sin ella,
 * PERO SOLO EN EL NAVEGADOR.
 *
 * Antes redirigía al portal en cuanto había sesión, y eso hacía que el dueño
 * y cualquier cliente logueado no pudieran volver a ver su propio sitio: la
 * dirección de la empresa quedaba secuestrada por la aplicación. Son dos
 * cosas distintas y ahora conviven: acá el sitio, y la aplicación en
 * `/admin`, `/portal` y `/empleado`.
 *
 * Quien ya tiene sesión no ve «Ingresar» —ya ingresó—: la landing le ofrece
 * entrar a lo suyo. Eso lo resuelve `PublicLanding` con la sesión.
 *
 * Instalada como PWA (3-sep-2026, «el inicio no debería ser landing page,
 * sino el login... al estar iniciado su sesión, el portal que corresponda»)
 * es otra historia: quien la abre desde el ícono ya decidió que es SU
 * aplicación, no un visitante llegando por primera vez — ahí el sitio de
 * mercadeo no tiene nada que hacer, y el arranque va directo a entrar o al
 * panel de quien ya entró.
 */
function Landing() {
  const { user, loading } = useSession();
  if (loading) return <Loading />;
  if (esStandalone()) {
    return <Navigate to={user ? homeFor(user.role) : '/ingresar'} replace />;
  }
  return <PublicLanding />;
}

export function App() {
  const navigate = useNavigate();
  const { user } = useSession();

  /* Dos cosas de los avisos nativos, las dos silenciosas:
     · tocar un aviso navega DENTRO de la aplicación en vez de recargarla — el
       service worker manda el destino por mensaje y acá se atiende;
     · la suscripción se reafirma en cada arranque con sesión, que es lo que
       arregla el caso en que el navegador rota las claves por su cuenta y la
       fila vieja deja de servir sin que nada avise. */
  useEffect(() => escucharAlServiceWorker((url) => navigate(url)), [navigate]);
  useEffect(() => { if (user) void reafirmarPush(); }, [user]);

  return (
    <>
      {/* Solo con sesión: sin ella no hay a quién avisarle, y además la
          consulta que decide si mostrarla necesita estar autenticada. */}
      {user && <InvitarAvisos />}
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/privacidad" element={<PrivacyPage />} />
      <Route path="/terminos" element={<TermsPage />} />
      <Route path="/ingresar" element={<LoginPage />} />
      <Route path="/invitacion" element={<AcceptInvitePage />} />
      <Route path="/invitacion-consultorio" element={<JoinUnitPage />} />
      <Route path="/restablecer" element={<ResetPasswordPage />} />

      {/* ── Administración ─────────────────────────────────────────────── */}
      <Route path="/admin" element={<AdminPage crumbs={['Panel']}><AdminDashboard /></AdminPage>} />
      <Route path="/admin/clientes" element={<AdminPage crumbs={['Clientes']}><ClientsPage /></AdminPage>} />
      <Route path="/admin/consultorios/:id" element={<AdminPage crumbs={['Clientes', 'Consultorio']}><UnitDetailPage /></AdminPage>} />
      <Route path="/admin/doctores/:id" element={<AdminPage crumbs={['Clientes', 'Doctor']}><DoctorProfilePage /></AdminPage>} />
      {/* Rutas viejas: «Consultorios» y «Doctores» eran dos destinos separados
          y ahora son pestañas de uno solo. Un enlace guardado a cualquiera de
          los dos sigue aterrizando en el lugar correcto. */}
      <Route path="/admin/consultorios" element={<Navigate to="/admin/clientes" replace />} />
      <Route path="/admin/doctores" element={<Navigate to="/admin/clientes?tab=doctores" replace />} />
      <Route path="/admin/comunicacion" element={<AdminPage crumbs={['Comunicación']}><MessagesPage /></AdminPage>} />
      <Route path="/admin/contratos" element={<AdminPage crumbs={['Paquetes']}><ContractsPage /></AdminPage>} />
      <Route path="/admin/contratos/:id" element={<AdminPage crumbs={['Paquetes', 'Detalle']}><ContractDetailPage /></AdminPage>} />
      {/* ── Cobros: un workspace, no cinco destinos ─────────────────────── */}
      <Route
        path="/admin/cobros"
        element={<AdminPage crumbs={['Cobros']}><CobrosLayout /></AdminPage>}
      >
        <Route index element={<MatrixPage />} />
        <Route path="por-hacer" element={<ActionsPage />} />
        <Route path="mes" element={<ChargesPage />} />
        <Route path="generar" element={<GeneratePage />} />
        <Route path="pagos" element={<PaymentsPage />} />
        <Route path="facturacion" element={<InvoicingPage />} />
        <Route path="conciliacion" element={<ReconciliationPage />} />
      </Route>
      <Route path="/admin/cobros/detalle/:id" element={<AdminPage crumbs={['Cobros', 'Detalle']}><ChargeDetailPage /></AdminPage>} />

      {/* Rutas viejas: siguen funcionando. Un enlace guardado o un marcador no
          tiene por qué romperse porque cambiamos la navegación. */}
      <Route path="/admin/generar" element={<Navigate to="/admin/cobros/generar" replace />} />
      <Route path="/admin/pagos" element={<Navigate to="/admin/cobros/pagos" replace />} />
      <Route path="/admin/facturacion" element={<Navigate to="/admin/cobros/facturacion" replace />} />
      <Route path="/admin/conciliacion" element={<Navigate to="/admin/cobros/conciliacion" replace />} />
      <Route path="/admin/finanzas" element={<AdminPage crumbs={['Finanzas']}><FinancePage /></AdminPage>} />
      <Route path="/admin/obligaciones" element={<AdminPage crumbs={['Obligaciones']}><ObligationsPage /></AdminPage>} />
      <Route path="/admin/inventario" element={<AdminPage crumbs={['Inventario']}><InventoryPage /></AdminPage>} />
      <Route path="/admin/personal" element={<AdminPage crumbs={['Personal']}><StaffPage /></AdminPage>} />
      <Route path="/admin/calidad" element={<AdminPage crumbs={['Calidad']}><QualityPage /></AdminPage>} />
      <Route path="/admin/incidencias" element={<AdminPage crumbs={['Incidencias']}><IncidentsPage /></AdminPage>} />
      <Route path="/admin/solicitudes" element={<AdminPage crumbs={['Solicitudes']}><RequestsPage /></AdminPage>} />
      <Route path="/admin/calendario" element={<AdminPage crumbs={['Calendario']}><CalendarPage /></AdminPage>} />
      <Route path="/admin/reportes" element={<AdminPage crumbs={['Reportes']}><ReportsPage /></AdminPage>} />
      <Route path="/admin/catalogo" element={<AdminPage crumbs={['Catálogo']}><CatalogPage /></AdminPage>} />
      <Route path="/admin/proveedores" element={<AdminPage crumbs={['Proveedores']}><SuppliersPage /></AdminPage>} />
      <Route path="/admin/configuracion" element={<AdminPage crumbs={['Configuración']}><SettingsPage /></AdminPage>} />
      <Route path="/admin/perfil" element={<AdminPage crumbs={['Mi perfil']}><MyProfilePage /></AdminPage>} />

      {/* ── Portal del cliente ─────────────────────────────────────────── */}
      {/* Sin ADMIN, a propósito. Estaba desde el primer día como comodidad
          —«que el dueño pueda mirar el portal»— pero el portal rotula todo en
          primera persona y el dueño no es cliente de sí mismo: entraba y leía
          la cartera entera de la empresa como si fuera lo que él debe
          (₡6.054.525; reporte del dueño, 8-set-2026). Para ver la experiencia
          del cliente está el usuario de prueba, que además muestra lo que un
          cliente ve de verdad y no una mezcla. La garantía de fondo no es esta
          línea sino `soloLoPropio` en el Worker: aunque alguien llegue igual,
          el portal contesta lo propio y nada más. */}
      <Route
        path="/portal"
        element={
          <RequireRole roles={['CLIENT_ADMIN', 'CLIENT_MEMBER']}>
            <PortalLayout />
          </RequireRole>
        }
      >
        <Route index element={<PortalHome />} />
        {/* Lista y detalle anidados: en escritorio se ven lado a lado
            (master-detail); en el teléfono siguen siendo dos pantallas. */}
        <Route path="cobros" element={<PortalCharges />}>
          <Route index element={<PortalChargesIndex />} />
          <Route path=":id" element={<PortalChargeDetail />} />
        </Route>
        <Route path="servicios" element={<PortalServices />} />
        <Route path="mejora" element={<PortalImprove />} />
        <Route path="mas" element={<PortalMore />} />
        {/* «Mi perfil» del cliente vive adentro de la solapa «Cuenta» de
            `/portal/mas`, así que esta ruta queda solo para los enlaces
            viejos. */}
        <Route path="perfil" element={<Navigate to="/portal/mas" replace />} />
      </Route>

      {/* ── Portal del empleado ────────────────────────────────────────── */}
      <Route
        path="/empleado"
        element={
          <RequireRole roles={['EMPLOYEE', 'ADMIN']}>
            <EmployeeLayout />
          </RequireRole>
        }
      >
        <Route index element={<EmployeeHome />} />
        <Route path="insumos" element={<EmployeeInsumos />} />
        <Route path="horas" element={<EmployeeOvertime />} />
        <Route path="vacaciones" element={<EmployeeVacations />} />
        {/* La pantalla unificada de CB-21 reemplaza al viejo
            `features/employee/Profile.tsx`, que dejaba editar el correo —
            contra la decisión P6.4 (correo solo lo cambia un admin). */}
        <Route path="perfil" element={<MyProfileMobilePage />} />
        <Route path="visitas/:id" element={<EmployeeVisit />} />
      </Route>

      <Route path="*" element={<Landing />} />
    </Routes>
    </>
  );
}
